import { factor, isNegative, isZero, money } from '@fameacc/money';
import { z } from 'zod';
import { customerTypeSchema } from './master-data.js';
import { priceBasisSchema, documentLineTypeSchema, taxScenarioSchema, taxRateFactor, unitPriceAmount, } from './documents.js';
import { isoDate, nonNegativeAmount } from './posting.js';
/**
 * Der Vertrag von `POST /api/v1/invoices/import` — die **kanonische Form** der
 * Altrechnungen-Ära (Spec 5.1/5.2, Aufgabe `AI-import`).
 *
 * **Warum kanonisch und nicht das Alt-Schema.** Der Endpunkt darf nicht
 * wissen, dass es `data_rechnungspositionen` gibt. Die Zerlegung des
 * PHP-serialisierten Positionsblocks ist Sache von `@fameacc/altimport-parser`
 * — eines Pakets **ohne** Datenbank, ohne HTTP und ohne Nest, das vollständig
 * über Unit-Tests prüfbar ist. Käme das rohe Feld über HTTP, liefe der
 * PHP-Unserialize im Serverprozess, und der teuerste Fehler dieses Vorhabens
 * (eine falsch gelesene Betragsform) wäre erst hinter einer Netzgrenze
 * prüfbar.
 *
 * **Die Form ist nah an `document` und nicht an `fa_rechnungen`.** Jedes Feld
 * unten hat ein Ziel in der Belegtabelle; was der Altbestand sonst noch führt
 * — `update_status`, `bill_type`, `directory_hash` —, kommt hier nicht an. Der
 * `directory_hash` ausdrücklich nicht: Er ist der einzige Zugangsschutz der
 * öffentlich erreichbaren Alt-PDFs (Spec 13, `D-26`), und die PDF-Beschaffung
 * ist eine eigene Aufgabe. **Was seit `KP-altlinkpush` (2026-09-25) ankommt, ist
 * sein SHA-256** ({@link altimportBelegSchema}`.altlinkHashSha256`): Das
 * Altsystem rechnet `hash('sha256', directory_hash)` bei sich und schickt nur
 * das Ergebnis — daraus lässt sich der Hash nicht zurückrechnen, und mit ihm
 * holt niemand ein PDF (zweiter datierter Nachtrag zu `D-26`).
 *
 * **Beträge sind Zeichenketten und verlassen das Schema als `Money`**
 * (Hausregel 1). Das ist bei dieser Ära kein Formalismus, sondern das
 * Geschenk: Die Alt-Preise stehen als Text im PHP-Block, mit bis zu fünfzehn
 * Nachkommastellen. Ein `parseFloat` darauf ist ein roter Lauf, auch in einer
 * Prüfdatei.
 *
 * **Alle Objekte sind `.strict()`.** Anders als bei der Ereignisablage
 * (`ingest.ts`), wo ein unbekanntes Feld der Normalfall eines fremden Systems
 * ist, ist der Absender hier **unser eigener** Importlauf: Ein Feld, das der
 * Vertrag nicht kennt, ist dort ein Tippfehler und keine Nutzlast — und ein
 * stillschweigend verworfenes Feld wäre ein Beleg, dem etwas fehlt, ohne dass
 * es jemand merkt.
 */
/**
 * Die Quelle, unter der Altbelege eingespielt werden.
 *
 * Sie ist zugleich `document.source_system` und die halbe Hälfte des
 * Idempotenzschlüssels (`D-21`, `E-638`). Ein `z.literal` und kein freier Text:
 * Der Teilindex `document_altimport_quelle` (`0121`) ist auf genau diesen Wert
 * verengt, und ein zweiter Quellname bekäme still keine Idempotenz.
 */
export const ALTIMPORT_QUELLE = 'altsystem-import';
/**
 * Der Herkunftsraum der Alt-Kundennummer (`B-1`, `D-11`).
 *
 * | Wert | Alt-Nummernraum | Belege |
 * |---|---|---|
 * | `famesystems` | vier- und fünfstellig | 10.864 |
 * | `altsystem` | sechsstellig (`1001xx`) | 359 |
 *
 * Die einstelligen Nummern (37 Belege) stehen hier **nicht**: Sie gehören in
 * keinen der beiden Räume und sind ein blockierender Befund, kein dritter
 * Wert.
 *
 * **Bewusst eine eigene Aufzählung und nicht `customerOriginSchema`.** Diese
 * hier ist eine **echte Teilmenge**: `manual` — „im neuen System von Hand
 * angelegt" — ist kein Herkunftsraum des Altbestands, und ein Altbeleg, der ihn
 * nennte, verlangte eine Auflösung in einem Raum, in dem seine Nummer nichts
 * bedeutet. Sie sagt nur, in welchem Zahlenraum die Alt-Nummer zu suchen ist;
 * sie legt nichts darüber fest, wie ein Kunde angelegt oder angezeigt wird
 * (`AR-11`).
 *
 * Seit `AI-kunden` (2026-09-02) kennt `customerOriginSchema` den Wert
 * `altsystem` ebenfalls — vorher lebte er allein im Datenbank-Enum (`0120`),
 * und die erste gelesene Kundenzeile mit dieser Herkunft hätte
 * `customerViewSchema` zerbrochen.
 */
export const ALTIMPORT_KUNDENHERKUENFTE = ['famesystems', 'altsystem'];
/** Der Herkunftsraum, in dem die Alt-Kundennummer gesucht wird. */
export const altimportKundenherkunftSchema = z.enum(ALTIMPORT_KUNDENHERKUENFTE);
/**
 * Welche der drei Alt-Tabellen diesen Beleg geliefert hat — und damit, welche
 * **Archivklasse** er wird (`D-4`, `D-16`, Aufgabe `AI-storno`).
 *
 * | Wert | Alt-Tabelle | `document.type` | Nummernkreis | Sätze |
 * |---|---|---|---|---|
 * | `rechnung` | `fa_rechnungen` | `legacy_invoice` | `document_no_invoice` | 11.260 |
 * | `storno` | `fa_storno` | `legacy_cancellation` | `document_no_legacy_cancellation` | 77 |
 * | `gutschrift` | `fa_gutschrift` | `legacy_credit_note` | `document_no_legacy_credit_note` | 5 |
 *
 * **Warum das ein Feld des Aufrufers sein DARF, obwohl die Klasse es nicht
 * ist.** Spec 5.1 verbietet ausdrücklich ein Flag, mit dem der Aufrufer wählt,
 * ob ein Beleg gebucht wird oder nicht — sonst könnte er eine 2026er-Rechnung
 * als Archivbeleg einspielen und ihre Buchung unterschlagen. Diese Angabe hier
 * ist etwas anderes: Sie sagt, welche BELEGART der Altbestand geführt hat, und
 * alle drei Werte sind Archivbelegarten (`ist_archivbelegtyp()`, `0119`). Die
 * Buchungsfrage bleibt unberührt — `pruefeKlasse()` weist jeden Beleg ab,
 * dessen Datum jenseits von `outgoing_scope.journal_from_date` liegt, und zwar
 * für alle drei Werte gleich. Es gibt damit keinen Weg, über diese Angabe an
 * einer Buchung vorbeizukommen.
 *
 * Ein eigenes Feld und nicht aus `fremdschluessel` abgeleitet: Gemessen am
 * Dump vom 01.09.2026 kollidiert **keine** der 77 Storno- und keine der 5
 * Gutschrift-`id` mit einer Rechnungs-`id` — aber die blossen SEQUENZEN tun es
 * ausnahmslos (`B-2`), und eine Ableitung aus der Zahl selbst wäre eine Regel,
 * die genau so lange trägt, wie dieser eine Dump gilt.
 */
export const ALTIMPORT_BELEGARTEN = ['rechnung', 'storno', 'gutschrift'];
/** Welche Alt-Tabelle den Beleg geliefert hat. */
export const altimportBelegartSchema = z.enum(ALTIMPORT_BELEGARTEN);
/** Trimmt und zieht Folgen von Leerraum auf ein Leerzeichen zusammen. */
function straffen(wert) {
    return wert.trim().replace(/\s+/g, ' ');
}
/**
 * Ein Pflichttextfeld.
 *
 * **Leer heisst Ablehnung und nicht Platzhalter**, und das ist an dieser
 * Stelle die tragende Entscheidung: `D-19` verlangt für ein im Altbestand
 * leeres Pflichtfeld einen **sichtbaren** Platzhalter (`[im Altsystem leer]`)
 * plus einen Befund — gesetzt wird er aber von der Kundenauflösung
 * (`AI-kunden`), die den Altbestand liest, nicht von diesem Vertrag. Ein
 * Vertrag, der selbst einen Platzhalter einsetzte, machte aus einem Befund
 * einen stillen Erfolg.
 */
const pflichtText = (feld) => z.string().transform((wert, ctx) => {
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/**
 * Ein Wahlfeld: fehlt, ist `null`, oder trägt einen nicht-leeren Text.
 *
 * Die leere Zeichenkette wird zu `null` und nicht durchgereicht —
 * `document_optional_text_not_blank` (`0022`) weist sie ab, und der Altbestand
 * trägt sie reichlich (`D-19`, gemessen 130 von 11.260 Belegen mit
 * USt-IdNr., der Rest leer statt `NULL`).
 */
const wahlText = z
    .string()
    .nullish()
    // `.default(null)` und nicht bloss `.nullish()`: Unter
    // `exactOptionalPropertyTypes` bleibt der Schlüssel sonst OPTIONAL, und der
    // abgeleitete Typ trägt `undefined` neben `null` — zwei Schreibweisen für
    // dasselbe „nicht angegeben", von denen die Datenbank nur eine kennt. Mit
    // dem Vorgabewert verlässt das Schema immer mit `string | null`.
    .default(null)
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const gestrafft = straffen(wert);
    return gestrafft === '' ? null : gestrafft;
});
/**
 * Die USt-IdNr.: **jeder** Leerraum weg, Grossschreibung, leer wird `null`.
 *
 * Nicht `wahlText`, und der Unterschied ist gemessen: Der Altbestand führt die
 * Nummer ausnahmslos mit einem Leerzeichen zwischen Präfix und Ziffern (130 von
 * 11.260 Belegen tragen überhaupt eine, alle mit Leerzeichen). `wahlText` zöge
 * Leerraum nur zusammen und liesse das eine Zeichen stehen —
 * `vat_id_wellformed_v1` erwartet aber den bereits normalisierten Wert, und
 * `document_billing_vat_id_wellformed` (`0022`) wiese **alle 130** ab, mitten
 * im Stapel und mit einer Meldung über eine Bedingung statt über das Feld.
 *
 * **Die Form wird hier nicht geprüft**, nur normalisiert. Gemessen am 2026-09-02
 * bestehen alle acht verschiedenen Werte des Bestands `vat_id_wellformed_v1`
 * nach dieser Normalisierung; eine Prüfung an dieser Stelle wiese im Fehlerfall
 * den ganzen Stapel ab statt eines Belegs, und eine Befundart für „USt-IdNr.
 * unbrauchbar" gibt es in `altimport_befundart` (noch) nicht.
 */
const ustIdText = z
    .string()
    .nullish()
    .default(null)
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const eng = wert.replace(/\s+/gu, '').toUpperCase();
    return eng === '' ? null : eng;
});
/**
 * Die E-Mail des Rechnungsempfängers am Altbeleg (`CU-belegmail`, 2026-09-04,
 * Migration `0157`): getrimmt, **kleingeschrieben**, leer wird `null`.
 *
 * **Die Form wird hier nicht geprüft, nur normalisiert** — dieselbe
 * Arbeitsteilung wie bei {@link ustIdText} und aus demselben Grund: Eine
 * Prüfung an dieser Stelle wiese den ganzen STAPEL ab statt eines Belegs. Ein
 * unbrauchbarer Wert wird eine Stufe früher zu `null` plus dem nicht
 * blockierenden Befund `email_ungueltig` (`0157`), im Zubringer
 * (`rechnungszubringer.ts`), wo eine Befundzeile überhaupt entstehen kann.
 *
 * **Kleingeschrieben**, weil `customer.email` eine `citext`-Spalte ist (`0014`)
 * und der Vergleich dort ohnehin ohne Rücksicht auf Gross-/Kleinschreibung
 * läuft; `document.billing_email` ist `text`, und zwei Schreibweisen derselben
 * Adresse wären dort zwei verschiedene Werte. Der Domänenteil einer
 * E-Mail-Adresse ist nach RFC 1035 ohnehin nicht schreibungsempfindlich; der
 * lokale Teil darf es theoretisch sein, in der Praxis führt ihn kein
 * verbreiteter Anbieter so, und ein Altbestand, der dieselbe Adresse einmal
 * gross und einmal klein schreibt, ist der Fall, gegen den diese Zeile steht.
 *
 * **Nullish mit Vorgabe `null`**: Ein Aufrufer, der das Feld nicht sendet,
 * bekommt dieselbe Aussage wie einer, der es leer sendet — der Altbestand
 * benutzt `NULL` und die leere Zeichenkette gleichermassen für „nicht erfasst"
 * (siehe `Altkopf` in `@fameacc/altimport-parser`).
 */
const emailText = z
    .string()
    .nullish()
    .default(null)
    .transform((wert) => {
    if (wert === undefined || wert === null)
        return null;
    const eng = wert.trim().toLowerCase();
    return eng === '' ? null : eng;
});
/**
 * Eine Menge: Faktor, echt über null. Das Vorzeichen steckt im Preis.
 *
 * So hält es der Altbestand, und so hält es `document_line_quantity_positive`.
 * Verglichen wird dezimal über `packages/money`, nie über `Number` — dieselbe
 * Kette wie in `documents.ts`.
 */
const mengeFaktor = z.string().transform((wert, ctx) => {
    let menge;
    try {
        menge = factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Keine gültige Menge' });
        return z.NEVER;
    }
    const alsBetrag = money(menge);
    if (isNegative(alsBetrag) || isZero(alsBetrag)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Die Menge muss größer als null sein' });
        return z.NEVER;
    }
    return menge;
});
/**
 * Die eingefrorene Anschrift aus dem Alt-Rechnungskopf (`D-12`).
 *
 * **Aus dem Kopf und nie aus einem Stammsatz.** 96,8 % der Altbelege haben
 * ohnehin keinen Stammsatz, und der Kopf zeigt, was auf dem Beleg stand — nicht,
 * wie der Kunde heute heisst. Die Felder landen unverändert in den
 * `billing_*`-Spalten von `document` und sind ab dem `COMMIT` unveränderlich.
 *
 * **`land` ist ein ISO-Code und kein Ländername.** Die 120 gemessenen
 * Schreibweisen des Altbestands — deutsch, englisch, niederländisch,
 * italienisch, spanisch, tschechisch, rumänisch, türkisch, arabische Schrift,
 * Klammerformen, ein Tippfehler — werden **vor** diesem Vertrag zugeordnet
 * (`D-14`, Aufgabe `AI-kunden`). Ein Vertrag, der Ländernamen entgegennähme,
 * müsste raten, und `Georgia` ist aus dem Wert allein nicht entscheidbar.
 *
 * **`email` seit `CU-belegmail` (2026-09-04, Migration `0157`).** Alle drei
 * Alt-Tabellen (`fa_rechnungen`, `fa_storno`, `fa_gutschrift`) führen die
 * Spalte; sie landet in `document.billing_email` und ist ab dem `COMMIT`
 * unveränderlich — mit dem einen erlaubten Nachtrag `NULL → Wert`, den der
 * Wiedereinstieg braucht (`0157`, Abschnitt „EINMAL SETZBAR"). Sie steht
 * ausdrücklich **nicht** auf dem gedruckten Beleg (Owner-Entscheid
 * 2026-09-05).
 */
export const altimportAnschriftSchema = z
    .object({
    anzeigeKundennummer: pflichtText('anzeigeKundennummer'),
    name: pflichtText('name'),
    ansprechpartner: wahlText,
    anrede: wahlText,
    strasse: pflichtText('strasse'),
    adresszusatz: wahlText,
    plz: pflichtText('plz'),
    ort: pflichtText('ort'),
    land: z
        .string()
        .transform((v) => v.trim().toUpperCase())
        .pipe(z.string().regex(/^[A-Z]{2}$/, 'Länderkennzeichen besteht aus zwei Buchstaben')),
    ustId: ustIdText,
    kundenart: customerTypeSchema,
    email: emailText,
})
    .strict();
/**
 * Eine Position in kanonischer Form — genau das, was
 * `liesPositionen()` aus `@fameacc/altimport-parser` liefert.
 *
 * **`art: 'text'` genau bei Preis null** (`E-647`). Eine Textzeile trägt weder
 * Menge noch Preis; das erzwingt die Querprüfung unten, weil
 * `document_line_text_has_no_amounts` (`0022`) sie sonst am `COMMIT` abwiese —
 * mit einer Meldung über eine Bedingung statt über das Feld, das fehlt.
 */
export const altimportPositionSchema = z
    .object({
    beschreibung: pflichtText('beschreibung'),
    art: documentLineTypeSchema,
    menge: mengeFaktor.nullish().default(null),
    einzelpreis: unitPriceAmount.nullish().default(null),
})
    .strict()
    .superRefine((position, ctx) => {
    const istText = position.art === 'text';
    if (istText && (position.menge != null || position.einzelpreis != null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Textzeile trägt weder menge noch einzelpreis',
        });
    }
    if (!istText && (position.menge == null || position.einzelpreis == null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Sachposition braucht menge und einzelpreis',
        });
    }
});
/**
 * Ein Befund, den der **Leser** schon gefunden hat und den der Endpunkt in die
 * Fehlliste schreibt (`AI-kunden`, Spec 10.1).
 *
 * **Warum der Aufrufer Befunde mitbringt.** Die Zuordnung des Ländernamens, die
 * Platzhalter für leere Pflichtfelder und die Regel für eine Fälligkeit vor dem
 * Belegdatum geschehen **vor** diesem Vertrag — sonst nähme er Ländernamen
 * entgegen und müsste raten (`altimportAnschriftSchema.land`). Was der Leser
 * dabei findet, hätte ohne dieses Feld keinen Weg in `altimport_befund`, und die
 * Vollständigkeitsgleichung (`importierte Belege + Fehllistenzeilen = 11.260`,
 * `D-22`) zählte einen stillschweigend geänderten Beleg als sauberen Import.
 *
 * **Der Aufrufer sendet nur `art` und `feld`, nie einen Text.** Beschreibung und
 * Blockierendheit stehen in `altimport_befundart` (`0120`) und werden dort
 * gelesen. Zwei Gründe, und beide tragen: Ein Freitext des Aufrufers könnte
 * einen Klartextwert aus dem Altbestand über die Netzgrenze tragen (Spec 13,
 * `D-26`), und ein Aufrufer, der die Blockierendheit mitschickte, könnte einen
 * Befund als nicht blockierend erklären, den die Spec blockierend nennt.
 */
export const altimportBefundSchema = z
    .object({
    /**
     * Die Art aus `altimport_befundart`. Die Form ist dieselbe wie
     * `altimport_befundart_shape`; **ob** es die Art gibt, entscheidet die
     * Datenbank und nicht dieser Vertrag — eine hier abgeschriebene Werteliste
     * wäre die zweite Wahrheit über eine Tabelle, die je Aufgabe wächst.
     */
    art: z.string().regex(/^[a-z][a-z0-9_]{2,60}$/, 'Keine gültige Befundart'),
    /** Die betroffene Spalte, oder `null` für einen Befund an der Zeile als Ganzes. */
    feld: wahlText,
})
    .strict();
/**
 * Ein Altbeleg in kanonischer Form (Spec 5.2).
 *
 * **Was hier NICHT steht, und warum das die wichtigste Zeile dieses Vertrags
 * ist:** die Klasse. Ob ein Beleg Archivbeleg oder gebuchter Beleg wird,
 * ergibt sich aus `belegdatum` und `outgoing_scope.journal_from_date` — **nicht
 * aus einem Flag des Aufrufers** (Spec 5.1). Ein Aufrufer, der die Klasse
 * wählen dürfte, könnte eine Rechnung der gebuchten Klasse als Archivbeleg
 * einspielen und damit ihre Buchung unterschlagen.
 */
/**
 * **Die Mahndaten des Altsystems an einer Altrechnung** — Auftrag
 * `AI-mahnaltdaten` (2026-09-05), Migration `0158`.
 *
 * Anlass ist eine Auskunft des Auftraggebers vom 2026-09-05: `fa_rechnungen`
 * führt mit `mahnung` und `mahngebueren` zwei Spalten, die bis dahin kein
 * Zubringer gelesen hat, und das Altsystem zeigt daneben einen Zinsbetrag, den
 * es **live rechnet** und nirgends speichert. Gemessen am Dump vom 2026-09-03:
 * 137 Rechnungen mit `mahnung = 1`, davon 67 offen; Gebühren 0,00 / 2,50 /
 * 5,00 / 7,50 €.
 *
 * ## Herkunftsangaben und keine Buchung
 *
 * **Auftraggeberentscheid vom 2026-09-05:** Mahnkosten und Verzugszinsen der
 * Altrechnungen gehen **nicht** in die Forderung und den Anfangsbestand ein
 * und werden beim Import **nicht** gebucht. Sie werden erst **bei Zahlung**
 * als nicht steuerbare sonstige Erträge gebucht — Sache der kommenden
 * Zahlungszuordnungs-Ära. Am Archivbeleg stehen sie ausschliesslich als
 * Herkunftsangaben.
 *
 * Deshalb berührt dieser Block `positionen`, `steuersatz` und damit den
 * Bruttobetrag des Belegs an **keiner** Stelle: `document.gross_total` einer
 * importierten Altrechnung enthält weder Gebühr noch Zins (`E-841`).
 *
 * ## Der Zins trägt seinen Stichtag mit
 *
 * `zinsen` ist ein **Schnappschuss** und kein Bestand. Das Altsystem rechnet
 * den Betrag bei jedem Aufruf neu, und zwar — gemessen — auch an bereits
 * bezahlten Rechnungen weiter (siehe `berechneVerzugszinsen()` in
 * `apps/api/src/cutover/rechnungszubringer.ts`). Eine Zahl ohne den Tag, auf
 * den sie gerechnet ist, wäre damit eine Behauptung mit Verfallsdatum;
 * `zinsenStichtag` ist deshalb Pflicht und nicht wahlfrei.
 */
export const altimportMahndatenSchema = z
    .object({
    /**
     * Die Mahnstufe (1–3), ABGELEITET aus `fa_rechnungen.mahngebueren` —
     * NICHT der Rohwert von `mahnung` (Nachtrag `AI-zahldatum`, 2026-09-05,
     * Fall `E-899`). `fa_rechnungen.mahnung` ist im Dump nur ein FLAG
     * (`0`/`1`, ob überhaupt gemahnt wurde) und zählt die Mahnschreiben
     * nicht; die alte Fassung dieses Feldes übernahm diesen Rohwert wörtlich
     * und behauptete damit fälschlich eine Stufe. Die Ableitung
     * (`leiteMahnstufeAb()`, `apps/api/src/cutover/rechnungszubringer.ts`)
     * folgt der Auftraggeberregel vom 2026-09-05: `0,00` → 1
     * (Zahlungserinnerung), `0,01`–`2,50` → 2 (2. Mahnung), `≥ 7,50` → 3
     * (3. Mahnung, letzte). Der Bereich `(2,50; 7,50)` ist nicht benannt —
     * 2 der 137 Mahnrechnungen tragen genau `5,00` und werden vorläufig
     * Stufe 2 zugeordnet, mit dem nicht blockierenden Befund
     * `mahnstufe_uneindeutig` daneben.
     */
    stufe: z.number().int().min(1).max(3),
    /** `fa_rechnungen.mahngebueren`, als Betrag. `0.00` ist ein gemessener Wert (12 Belege). */
    gebuehr: nonNegativeAmount,
    /**
     * Der angewandte Verzugszinssatz in Prozent, als Text — `'4.12'` bei
     * Privat-, `'8.12'` bei Geschäftskunden (Auftraggeberangabe 2026-09-05).
     * Zwei Nachkommastellen, damit die Zeichenkette dieselbe Form hat wie die
     * Spalte `numeric(5,2)`.
     */
    zinssatz: z.string().regex(/^[0-9]{1,2}\.[0-9]{2}$/u, 'Der Zinssatz steht als Prozentzahl mit zwei Nachkommastellen'),
    /** Der nachgerechnete Verzugszins zum {@link altimportMahndatenSchema.shape.zinsenStichtag}. */
    zinsen: nonNegativeAmount,
    /** Der Tag, auf den {@link altimportMahndatenSchema.shape.zinsen} gerechnet ist. */
    zinsenStichtag: isoDate,
    /**
     * `fa_rechnungen.paid_date`, Datumsteil — der Tag, an dem das Altsystem
     * die Rechnung als bezahlt vermerkt hat.
     *
     * Er ist **nicht** Teil der Zinsrechnung (siehe oben: der Alt-Zins läuft
     * darüber hinaus weiter) und steht hier, weil erst er die angezeigte Zahl
     * lesbar macht. Getragen wird er ausschliesslich an Belegen, die diesen
     * Block überhaupt haben — `document.legacy_paid_at` sagt an jedem anderen
     * Beleg nichts über Zahlung aus (`document_legacy_paid_at_scope`, `0158`).
     */
    bezahltAm: isoDate.nullish().default(null),
})
    .strict();
/**
 * Der SHA-256 eines Altlink-Hashes: 64 Hexziffern, kleingeschrieben — die Form,
 * die PHP mit `hash('sha256', …)` und `node:crypto` mit `.digest('hex')`
 * liefern und die `document_altlink_hash_form` (`0234`) verlangt.
 *
 * **Der Klartext kommt nie über diese Grenze** (`D-26`, zweiter datierter
 * Nachtrag vom 2026-09-25, Kundenportal-Spec `D-2`). Ein Feld, das den
 * `directory_hash` selbst annähme, gäbe es in diesem Vertrag nicht; was das
 * Altsystem schickt, ist `hash('sha256', directory_hash)` — gerechnet über die
 * getrimmte Zeichenkette als UTF-8, dieselbe Rechnung wie
 * `apps/api/src/portal/altlinks-lib.ts::beurteileBeleg()` und wie die
 * Auflösung `POST /portal/altlinks/aufloesen`.
 *
 * Grossbuchstaben werden abgewiesen und nicht umgeschrieben: Die Spalte
 * vergleicht Zeichen für Zeichen, und ein Vertrag, der still normalisierte,
 * verdeckte einen Absender, der eine andere Rechnung benutzt als die
 * Auflösung.
 */
export const altlinkHashSha256Schema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'Der Altlink-Hash ist keine kleingeschriebene SHA-256 in 64 Hexstellen');
export const altimportBelegSchema = z
    .object({
    /** Fest `altsystem-import`. Wird `document.source_system`. */
    quelle: z.literal(ALTIMPORT_QUELLE),
    /**
     * Die Alt-`id` als Text. Wird `document.source_reference` **und**
     * `document.number` — die historische Nummer wird übernommen und nicht neu
     * gezogen (`E-639`).
     */
    fremdschluessel: pflichtText('fremdschluessel'),
    /**
     * Die Alt-`id` ab Stelle 5 — der Teil ohne Jahresvorsatz. Wird
     * `document.number_sequence` und ist der Wert, auf den der Zähler
     * fortgeschrieben wird (`D-5`, `E-640`).
     */
    nummernfolge: z.number().int().positive(),
    /**
     * `created_date` des Altbelegs, Datumsteil. Wird `issue_date` und
     * **entscheidet die Klasse** (`D-3`) — nicht der `id`-Vorsatz, der am
     * Kalendertag eines Cronjobs hängt und nicht am Beleg.
     */
    belegdatum: isoDate,
    /**
     * `paiduntil_date`. **Leer, wenn sie vor dem Belegdatum liegt** (`D-18`,
     * 33 Belege): `document_date_order` wird nicht gelockert, und der Befund
     * daneben steht in der Fehlliste.
     */
    faelligkeit: isoDate.nullish().default(null),
    kunde: z
        .object({
        herkunft: altimportKundenherkunftSchema,
        /** Die Alt-Kundennummer als Text — führende Nullen sind Teil der Nummer. */
        fremdnummer: pflichtText('kunde.fremdnummer'),
    })
        .strict(),
    anschrift: altimportAnschriftSchema,
    /**
     * `net` oder `gross`, **je Beleg** und nie je Position (`D-6`).
     * `document_basis_unique` bindet die Positionen über
     * `(document_id, price_basis)` daran; ein Beleg kann seine Basis nicht
     * mischen, und die 15 gemischten Altbelege sind deshalb ein Befund und
     * keine Modellierungsaufgabe (`D-16`).
     */
    preisbasis: priceBasisSchema,
    positionen: z.array(altimportPositionSchema).min(1, 'Ein Beleg ohne Positionen'),
    /** `fa_rechnungen.mwst` als Faktor — 19 % sind `0.19` und nicht `19`. */
    steuersatz: taxRateFactor,
    /** Der Steuerschlüssel der Zeile (`'3'` für 19 %, `'2'` für 7 %). */
    steuerschluessel: pflichtText('steuerschluessel'),
    /**
     * Abgeleitet, nicht übernommen (`D-15`). Die EU-B2C-Belege mit 19 %
     * bekommen `eu_b2c_domestic_vat` und nicht `domestic` — als `domestic`
     * verbucht verschwände ein Sachverhalt, der dem Steuerberater vorgelegt
     * wird.
     */
    steuerszenario: taxScenarioSchema,
    /** Das Erlöskonto der Positionen. */
    erloeskonto: z.string().regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern'),
    /**
     * `fa_rechnungen.status` **wörtlich**, `fa_storno.status`/
     * `fa_gutschrift.status` **abgebildet** (`D-2`, präzisiert am 2026-09-05,
     * Auftrag `AI-stornoaltstatus`, erweitert am selben Tag um Code `2` durch
     * `AI-gutschriftstatus`) — dieses Feld trägt in allen drei Fällen bereits
     * den KANONISCHEN, einen der fünf unten genannten Codes, nie den rohen
     * Tabellenwert. Getrennt von `document.status`: Der eine beschreibt den
     * Beleglebenszyklus, der andere die Zahlungslage des Altsystems.
     *
     * **Drei Tabellen, dieselbe Spalte `status`, DREI Domänen.** Der Dump
     * misst für `fa_storno` `-1` (Fehler), `0`/`1` (eingereicht) und `2`
     * (storniert/ausgeführt); für `fa_gutschrift` `-1` (Fehler), `0`
     * (eingereicht) und `1` (verschickt) — beides andere Bedeutungen als
     * dieselben Zahlen in `fa_rechnungen`. `altstatusFuer(belegart, roh)`
     * (`apps/api/src/cutover/rechnungszubringer.ts`) bildet jede Domäne
     * separat auf die Codes hier ab, BEVOR der Zubringer dieses Feld füllt;
     * ein Storno-Code `2` wird deshalb zu `4` (storniert), ein
     * Gutschrift-Code `1` zu `2` (erledigt) — zwei verschiedene Ziele für
     * denselben rohen Wert, weil es zwei verschiedene Domänen sind. Für
     * `fa_gutschrift`-Code `1` (verschickt) gab es bis `AI-gutschriftstatus`
     * (2026-09-05) KEINEN passenden Code — eine ausgeführte Gutschrift ist
     * weder bezahlt, storniert noch offen —, und der Fall blieb blockierender
     * Befund (`altstatus_unbekannt`). Der Controller hat den bis dahin
     * unbenannten, aber an der Spalte längst zugelassenen Code `2` seither für
     * genau diesen Fall benannt (`erledigt`, siehe
     * `legacyDocumentStatusSchema` in `documents.ts`).
     *
     * **Fünf benannte Codes, seit dem Torabwurf von Zug 141 (2026-09-04,
     * Auftrag `AI-zahlstatusanzeige`) auf vier verengt und seit
     * `AI-gutschriftstatus` (2026-09-05) um `2` erweitert.** Vorher liess
     * dieses Feld jede ganze Zahl von `-1` bis `5` durch — den vollen, an der
     * Datenbankspalte zugelassenen Bereich (`document_legacy_status_range`).
     * Zwei Testdateien nutzten `2` als beliebigen gültigen Wert; sobald die
     * Leseseite (`legacyStatusZuAnzeige`) begann, jeden nicht benannten Code
     * zu benennen statt zu raten, riss ein Beleg mit `altstatus: 2` beim
     * ERSTEN Lesen (mitten im Importlauf selbst, `AltimportService.bucheBeleg()`
     * liest den soeben angelegten Beleg zurück) die ganze Anfrage mit einem
     * 500 mit. Die Antwort ist NICHT, die Leseseite wieder zu lockern —
     * sondern die Schreibseite zu schärfen: `0` und `3` sind an der Spalte
     * weiterhin ERLAUBT (ein zweiter Dump kann sie tragen), aber sie haben
     * keine benannte Bedeutung, und dieser Vertrag ist der Ort, an dem neue,
     * unbenannte Werte gar nicht erst entstehen. Ein Wert ausserhalb der
     * fünf wird hier ABGEWIESEN und landet — im Zubringer
     * (`apps/api/src/cutover/rechnungszubringer.ts::liesAltstatus`) — als
     * benannte, blockierende Fehllistenzeile (`altstatus_unbekannt`) statt
     * als 400 mitten im Stapel. `istBenannterAltstatusCode` ist dieselbe
     * Prüfung, aus der auch `legacyStatusZuAnzeige` liest — Schreib- und
     * Leseseite laufen damit nicht auseinander.
     *
     * **`z.union` aus fünf `z.literal`n und nicht `z.number().refine(…)`.**
     * Ein `refine()` prüft zur Laufzeit richtig, verliert aber beim
     * OpenAPI-Export jede Angabe über den zulässigen Bereich — die generierte
     * Schemaseite zeigte `altstatus` sonst als unbeschränkte `integer`-Zahl,
     * obwohl sie enger ist als je zuvor. Die Literalunion wird als
     * `enum: [-1, 1, 2, 4, 5]` exportiert und dokumentiert die Zusicherung
     * damit an der Stelle, wo ein Aufrufer sie liest — dieselbe Erwägung, aus
     * der `documentInputSchema.type` ein `z.enum` ist und keine `z.string()`
     * mit Prüfung dahinter. Die Übereinstimmung mit `istBenannterAltstatusCode`
     * (derselben Menge in `packages/contracts/src/documents.ts`) hält
     * `packages/contracts/test/altimport.test.ts`.
     */
    altstatus: z.union([
        z.literal(-1),
        z.literal(1),
        z.literal(2),
        z.literal(4),
        z.literal(5),
    ]),
    /**
     * Was der Leser an diesem Beleg gefunden hat (`altimportBefundSchema`).
     *
     * `.default([])` und nicht `.optional()`: Unter `exactOptionalPropertyTypes`
     * bliebe der Schlüssel sonst optional, und der Dienst müsste an jeder
     * Fundstelle zwischen „keine Befunde" und „nicht angegeben" unterscheiden —
     * zwei Schreibweisen für dasselbe. Ein Stapel ohne dieses Feld verhält sich
     * damit genau wie vor `AI-kunden`.
     */
    befunde: z.array(altimportBefundSchema).max(50, 'Zu viele Befunde an einem Beleg').default([]),
    /**
     * Welche Alt-Tabelle den Beleg geliefert hat ({@link altimportBelegartSchema}).
     *
     * `.default('rechnung')` und nicht Pflicht: Ein Stapel ohne dieses Feld
     * verhält sich wortgleich wie vor `AI-storno` — 11.260 der 11.342 Belege
     * sind Rechnungen, und ein Vertrag, der die Mehrheit zwingt, sich als
     * Mehrheit auszuweisen, hätte jeden vorhandenen Aufrufer gebrochen, ohne
     * eine einzige Frage zu beantworten.
     */
    belegart: altimportBelegartSchema.default('rechnung'),
    /**
     * Nur bei `belegart: 'storno'`: welche Altrechnung dieser Storno-Satz
     * aufhebt, und ob **dieser** Satz derjenige ist, den das Altsystem selbst
     * als den wirksamen führt.
     *
     * ## Warum `fuehrend` ein Feld ist und keine Regel des Dienstes
     *
     * Gemessen tragen **4 der 73** stornierten Altrechnungen **zwei**
     * `fa_storno`-Sätze (77 Sätze, 73 Rechnungen). `document` kann aber nur
     * **einen** Storno vermerken: `document_cancelled_state` (`0022`) bindet
     * `status = 'cancelled'` an genau ein `cancelled_by_document_id`, und
     * `assert_document_transition` (`0034`) lässt es genau **einmal** setzen.
     *
     * Welcher der beiden es ist, wird **nicht geraten und nicht aus der
     * Reihenfolge genommen**: `fa_rechnungen.storno_id` nennt ihn, und zwar in
     * allen vier Fällen eindeutig. Der Leser trägt diese Antwort hier ein. Eine
     * Regel „der kleinere gewinnt" wäre in allen vier gemessenen Fällen die
     * FALSCHE gewesen — das Altsystem nennt jedes Mal den späteren Satz.
     *
     * Der zweite Satz geht deshalb nicht verloren: Er entsteht als eigener
     * Archivbeleg mit eigenem PDF und trägt seinen Bezug in
     * `document.predecessor_id`. Was er nicht bekommt, ist der Stornovermerk
     * AN DER RECHNUNG — den gibt es nur einmal, und er gehört dem Satz, den das
     * Altsystem führt.
     */
    storniert: z
        .object({
        /** Die Alt-`id` der stornierten Rechnung als Text (`fa_storno.rechnung_id`). */
        fremdschluessel: pflichtText('storniert.fremdschluessel'),
        /**
         * Nennt `fa_rechnungen.storno_id` **diesen** Satz? Dann setzt er den
         * Stornovermerk an der Rechnung; sonst ist er ein zweiter Archivbeleg
         * ohne Vermerk.
         */
        fuehrend: z.boolean(),
    })
        .strict()
        .nullish()
        .default(null),
    /**
     * Nur bei `belegart: 'gutschrift'`: der Bezugstext aus
     * `fa_gutschrift.invoice_id`, wörtlich und ungeprüft — **weiterhin als
     * Prosa am Beleg** (Spec 9.2 der Altimport-Spec), unabhängig davon, ob er
     * sich auflösen lässt.
     *
     * Die Spalte ist im Altsystem `varchar(255)`. **Korrigiert seit
     * `AI-gutschriftbezug` (2026-09-08):** Die frühere Lesart hier
     * („nicht rein numerisch, keiner der fünf Werte im Rechnungsbestand")
     * hatte das Kennzeichen `FA-` nicht abgeschnitten — alle fünf gemessenen
     * Werte tragen es vor einer reinen Ziffernfolge, und alle fünf lösen
     * danach zu einer vorhandenen `fa_rechnungen.id` auf (Messung im
     * Kopfkommentar von `apps/api/src/cutover/rechnungszubringer.ts`, Punkt 6).
     * `AltimportService` zieht denselben Schnitt
     * ({@link gutschriftFremdschluesselAusBezugstext} in
     * `rechnungszubringer.ts`) und setzt `document.predecessor_id`, wenn der
     * daraus abgeleitete Fremdschlüssel auf einen bereits importierten Beleg
     * zeigt; zeigt er ins Leere (kein auflösbares Kennzeichen, oder kein
     * Treffer im Bestand), bleibt `predecessor_id` leer, und der Beleg trägt
     * den nicht blockierenden Befund `gutschrift_ohne_ziel` (Migration
     * `0184`) — kein geratener Verweis, aber auch kein blockierter Beleg.
     *
     * Der Wert selbst geht in jedem Fall nicht verloren: Der Dienst legt ihn
     * unverändert als **Textposition** an den Archivbeleg
     * (`AltimportService.stelleAus`, über `positionenMitBezug()`). Damit steht
     * er dort, wo ein Mensch ihn sucht — am Beleg —, unabhängig davon, ob
     * `predecessor_id` zusätzlich eine geprüfte Beziehung trägt.
     */
    bezugstext: wahlText,
    /**
     * Die Mahndaten des Altsystems ({@link altimportMahndatenSchema}) —
     * `null` an jedem Beleg ohne Mahnstatus und an jedem Storno und jeder
     * Gutschrift (`fa_storno` und `fa_gutschrift` führen die Spalten nicht).
     *
     * `.nullish().default(null)` und nicht Pflicht: Ein Stapel ohne dieses
     * Feld verhält sich wortgleich wie vor `AI-mahnaltdaten` — 11.123 der
     * 11.260 Altrechnungen tragen keinen Mahnstatus.
     */
    mahndaten: altimportMahndatenSchema.nullish().default(null),
    /**
     * Der Zahlweg des Altsystems (`fa_rechnungen.bill_pay_method_text`) —
     * `null` an jedem Beleg ohne Angabe und an jedem Storno und jeder
     * Gutschrift (`fa_storno` und `fa_gutschrift` führen die Spalte nicht).
     * Auftrag `CU-einloesungen2026` (2026-09-05), Migration `0171`.
     *
     * **`wahlText` und kein Enum.** Die Alt-Spalte ist freier Text; der
     * Vertrag hält fest, was dort stand, und bildet es nicht auf eine Menge
     * ab, die das Altsystem nicht kennt. Wer eine Bedeutung braucht —
     * `pnpm cutover:einloesungen` sucht `Guthaben` — vergleicht beim Lesen
     * und **exakt**; jede andere Schreibweise ist dort ein Befund und kein
     * Treffer. Begründung im Kopf von `0171`.
     *
     * `.nullish().default(null)` und nicht Pflicht: Ein Stapel ohne dieses
     * Feld verhält sich wortgleich wie vor `CU-einloesungen2026`.
     */
    zahlweg: wahlText,
    /**
     * `fa_rechnungen.paid_date`, Datumsteil — der Tag, an dem das Altsystem
     * die Rechnung als bezahlt vermerkt hat (`AI-einloesungpush`, 2026-09-10,
     * Entscheidung `D-33` der Cutover-Spec).
     *
     * **Dieselbe Alt-Spalte wie {@link altimportMahndatenSchema.shape.bezahltAm},
     * an einem Ort, an dem sie JEDER bezahlte Beleg tragen kann.** Jenes Feld
     * steht im Mahnblock, weil `document.legacy_paid_at` dort GESPEICHERT wird
     * und `document_legacy_paid_at_scope` (`0168`) es an einen Mahnblock
     * bindet. Dieses Feld wird **nicht gespeichert** — es datiert die
     * Guthaben-Einlösung `1796` an `1400`, und die entsteht auch an einer
     * bezahlten Rechnung, die nie gemahnt wurde. Ohne dieses Feld müsste das
     * Buchungsdatum dort auf das Belegdatum zurückfallen; mit ihm ist es der
     * Tag der Zahlung, wie im Dump-Läufer (`pnpm cutover:einloesungen`).
     *
     * **Nur bei Altstatus `5` aussagekräftig** — dieselbe Grenze wie im
     * Mahnblock (`E-897`): `paid_date` trägt im Altbestand auch an offenen
     * Rechnungen einen Wert, und der ist dort kein Zahldatum. Der Zubringer
     * setzt es deshalb ausschliesslich bei Altstatus `5`.
     *
     * `.nullish().default(null)` und nicht Pflicht: Ein Stapel ohne dieses
     * Feld verhält sich wortgleich wie vor `AI-einloesungpush`.
     */
    bezahltAm: isoDate.nullish().default(null),
    /**
     * `sha256(directory_hash)` des Altsatzes — der Altlink-Hash, über den ein
     * Kunde seinen alten Rechnungslink im Kundenportal auflöst
     * (`KP-altlinkpush`, 2026-09-25, Kundenportal-Spec `D-2`,
     * {@link altlinkHashSha256Schema}). Wird `document.altlink_hash_sha256`.
     *
     * **Einmal gesetzt, nie überschrieben.** Beim Anlegen wird er gesetzt; an
     * einem SCHON VORHANDENEN Beleg (Wiedereinstieg) genau einmal von `NULL`
     * auf den Wert, derselbe Wert ist idempotent, ein ABWEICHENDER Wert und ein
     * Wert, der schon einem anderen Beleg gehört, werden gezählt und nicht
     * geschrieben (`altlinkAbweichend`, `altlinkDoppelt` im Bericht). Die
     * Regel steht genau einmal, in `apps/api/src/altimport/altlink-setzen.ts`.
     *
     * Für alle drei Belegarten und jeweils der Hash der EIGENEN Alt-Zeile —
     * `fa_storno.directory_hash` am Storno, nicht der der aufgehobenen
     * Rechnung.
     *
     * `.nullish().default(null)` und nicht Pflicht: Ein Stapel ohne dieses
     * Feld verhält sich wortgleich wie vor `KP-altlinkpush`, und eine Alt-Zeile
     * ohne `directory_hash` hat nichts zu schicken.
     */
    altlinkHashSha256: altlinkHashSha256Schema.nullish().default(null),
})
    .strict()
    .superRefine((beleg, ctx) => {
    // Die Querprüfungen der zwei neuen Klassen. Sie stehen hier und nicht im
    // Dienst, weil ein Beleg, der sie verletzt, gar nicht erst über die
    // Netzgrenze gehört: Ein Storno ohne Bezug wäre ein Archivbeleg, dessen
    // einzige Aufgabe — den Stornovermerk zu tragen — er nicht erfüllen kann.
    // Zwei Felder aus DERSELBEN Alt-Spalte (`fa_rechnungen.paid_date`) duerfen
    // nicht zwei verschiedene Tage sagen (`AI-einloesungpush`, `D-33`): Das
    // eine wird gespeichert (`legacy_paid_at`, an den Mahnblock gebunden), das
    // andere datiert die Guthaben-Einloesung. Zwei Wahrheiten ueber denselben
    // Tag waeren ein Beleg, der seiner eigenen Buchung widerspricht -- und der
    // Widerspruch fiele erst auf, wenn jemand die zwei Zahlen nebeneinander
    // legt. Ein NICHT gesetztes Feld ist kein Widerspruch: Der Mahnblock fehlt
    // an fast jedem Beleg.
    if (beleg.bezahltAm != null &&
        beleg.mahndaten?.bezahltAm != null &&
        beleg.bezahltAm !== beleg.mahndaten.bezahltAm) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bezahltAm'],
            message: 'bezahltAm und mahndaten.bezahltAm stammen aus derselben Alt-Spalte',
        });
    }
    if (beleg.belegart === 'storno' && beleg.storniert === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['storniert'],
            message: 'Ein Storno nennt die Altrechnung, die er aufhebt',
        });
    }
    if (beleg.belegart !== 'storno' && beleg.storniert !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['storniert'],
            message: 'Nur ein Storno nennt eine aufgehobene Altrechnung',
        });
    }
    if (beleg.belegart !== 'gutschrift' && beleg.bezugstext !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bezugstext'],
            message: 'Nur eine Gutschrift trägt einen Bezugstext',
        });
    }
});
/**
 * Der Rumpf des Endpunkts: ein Stapel von 1…n Altbelegen.
 *
 * **Ein Stapel und nicht ein Beleg je Anfrage.** 11.260 Einzelanfragen wären
 * 11.260 Transaktionen und 11.260 Beratungssperren; der Stapel ist die Einheit,
 * über die der Prüfbericht (unten) überhaupt eine Aussage machen kann.
 */
export const altimportRequestSchema = z
    .object({
    belege: z
        .array(altimportBelegSchema)
        .min(1, 'Der Stapel enthält keinen Beleg')
        .max(500, 'Höchstens 500 Belege je Stapel'),
})
    .strict();
/**
 * Was die EINE Setz-Regel für `document.altlink_hash_sha256` über einen Beleg
 * sagt — `POST /api/v1/invoices/import/altlinks` (`KP-altlinkpush`,
 * 2026-09-25).
 *
 * | Wert | Lage | geschrieben |
 * |---|---|---|
 * | `gesetzt` | der Beleg trug keinen Hash | ja |
 * | `unveraendert` | der Beleg trägt genau diesen Hash | nein — Idempotenz |
 * | `unbekannt` | unter diesem Fremdschlüssel führt dieses System keinen Altbeleg | nein |
 * | `abweichend` | der Beleg trägt einen ANDEREN Hash | nein — ein Befund, kein Überschreiben |
 * | `doppelt` | dieser Hash gehört schon einem anderen Beleg | nein — `document_altlink_hash_unique` |
 *
 * **Ein zweiter Lauf über denselben Bestand liefert nur `unveraendert`** (und
 * `unbekannt`, `abweichend`, `doppelt` unverändert wie beim ersten) — das ist
 * die Zusage, die einen abgebrochenen Nachschicklauf gefahrlos macht.
 */
export const ALTIMPORT_ALTLINK_ERGEBNISSE = [
    'gesetzt',
    'unveraendert',
    'unbekannt',
    'abweichend',
    'doppelt',
];
export const altimportAltlinkErgebnisSchema = z.enum(ALTIMPORT_ALTLINK_ERGEBNISSE);
/**
 * Der Prüfbericht als Antwort (Spec 5.4).
 *
 * **Vier Zahlen, deren erste die Summe der übrigen drei ist.** Ein Import, der
 * nur `201` sagt, ist von einem Import, der die Hälfte still verworfen hat,
 * nicht zu unterscheiden.
 *
 * | Zahl | Bedeutung |
 * |---|---|
 * | `gelesen` | Belege im Stapel |
 * | `angelegt` | neu entstandene Belege |
 * | `schonVorhanden` | vom Idempotenzschlüssel abgewiesen — ein zweiter Lauf (`E-637`) |
 * | `befunde` | Belege, die wegen eines **blockierenden** Befundes nicht entstanden sind |
 * | `nachgetragen` | schon vorhandene Belege, an denen `billing_email` von `NULL` auf einen Wert gesetzt wurde |
 * | `mahndatenNachgetragen` | schon vorhandene Belege, an denen die sechs Mahnspalten von `NULL` auf einen Wert gesetzt wurden |
 * | `zahlwegNachgetragen` | schon vorhandene Belege, an denen `legacy_pay_method` von `NULL` auf einen Wert gesetzt wurde |
 * | `altstatusNachgezogen` | schon vorhandene Belege, an denen `legacy_status` vorwärts nachgezogen wurde (`CU-deltalauf`) |
 * | `forderungNachgebucht` | schon vorhandene Belege der gebuchten Klasse, deren Forderung mit dem Statuswechsel nachgebucht wurde (`AI-nachzugbuchung`) |
 * | `kundenEmailGesetzt` | Kundenhüllen, die in diesem Lauf ENTSTANDEN sind und eine E-Mail tragen |
 * | `kundenEmailNachgetragen` | schon vorhandene Kunden, deren `email` von `NULL` auf einen Wert gesetzt wurde |
 * | `einloesungenGebucht` | Guthaben-Einlösungen `1796` an `1400`, die in diesem Lauf entstanden sind (`AI-einloesungpush`) |
 *
 * **`befunde` zählt Belege und nicht Fehllistenzeilen**, sonst ginge die
 * Gleichung nicht auf: Ein Altbeleg kann drei Befunde tragen und ist trotzdem
 * ein Beleg. Die Zeilen selbst zählt `altimport:befunde`, nach Art gruppiert.
 *
 * **`nachgetragen` steht NEBEN der Gleichung und nicht in ihr** (`CU-belegmail`,
 * 2026-09-04). Es ist eine **Teilmenge von `schonVorhanden`**: Ein Beleg, an dem
 * die E-Mail nachgetragen wurde, ist derselbe Beleg, den der
 * Idempotenzschlüssel abgewiesen hat — kein neuer. Wer die Zahl in die Summe
 * aufnähme, zählte ihn zweimal. Sie steht trotzdem im Bericht, weil ein
 * Wiedereinstieg über 8.519 Archivbelege sonst von einem Leerlauf nicht zu
 * unterscheiden wäre.
 *
 * **Die beiden Kundenzahlen zählen KUNDEN und nicht Belege** (`CU-belegmail`,
 * Erweiterung vom 2026-09-05, Befund aus `CU-debitorenmail`) und stehen
 * ebenfalls neben der Gleichung. Sie sind der einzige Weg, auf dem die 1.323
 * `famesystems`-Kunden je eine Adresse bekommen: `fa_kundenstammdaten` führt
 * nur die 110 sechsstelligen `altsystem`-Kunden (`B-1`), alle übrigen entstehen
 * als Hülle beim ersten Beleg zu einer noch unbekannten Alt-Nummer. Eine
 * vorhandene Adresse wird dabei **nie** überschrieben — sie kann von Hand
 * gepflegt sein.
 */
export const altimportBerichtSchema = z
    .object({
    gelesen: z.number().int().nonnegative(),
    angelegt: z.number().int().nonnegative(),
    schonVorhanden: z.number().int().nonnegative(),
    befunde: z.number().int().nonnegative(),
    nachgetragen: z.number().int().nonnegative(),
    /**
     * Wie `nachgetragen`, für die sechs Mahnspalten aus `0158`
     * (`AI-mahnaltdaten`) — ebenfalls eine **Teilmenge von `schonVorhanden`**
     * und deshalb neben der Gleichung.
     *
     * **Eigene Zahl und nicht mit `nachgetragen` zusammengelegt.** Die zwei
     * Nachträge treffen verschiedene Belegmengen: die E-Mail fast jeden
     * Altbeleg, die Mahndaten gemessen 137 von 11.260. Eine gemeinsame Zahl
     * sagte über keinen von beiden etwas.
     */
    mahndatenNachgetragen: z.number().int().nonnegative(),
    /**
     * Wie `nachgetragen`, für `legacy_pay_method` aus `0171`
     * (`CU-einloesungen2026`) — ebenfalls eine **Teilmenge von
     * `schonVorhanden`** und deshalb neben der Gleichung.
     *
     * **Eigene Zahl, dieselbe Begründung wie bei `mahndatenNachgetragen`:**
     * Der Zahlweg trifft eine dritte Belegmenge — jede Altrechnung mit
     * Zahlungsangabe, keine einzige Gutschrift und keinen Storno. Eine
     * gemeinsame Zahl sagte über keinen der drei Nachträge etwas.
     */
    zahlwegNachgetragen: z.number().int().nonnegative(),
    /**
     * Schon vorhandene Belege, an denen `legacy_status` in diesem Lauf
     * VORWÄRTS nachgezogen wurde (`CU-deltalauf`, Migration `0172`) — wieder
     * eine **Teilmenge von `schonVorhanden`** und deshalb neben der Gleichung.
     *
     * **Der Unterschied zu den drei Zahlen darüber, und er ist der Grund für
     * eine vierte:** Jene drei zählen einen Übergang `NULL` → Wert an einer
     * Spalte, die es beim ersten Import noch nicht gab. Diese zählt einen
     * Übergang zwischen zwei ECHTEN Werten — der Altbeleg war offen und ist
     * jetzt bezahlt oder storniert —, und sie ist damit die einzige Zahl des
     * Berichts, die eine Veränderung im ALTSYSTEM misst statt eine Lücke
     * dieses Systems. Ein Delta-Lauf, dessen Zubringer hier `0` melden,
     * hat nichts nachzuholen gehabt.
     *
     * Ein ABGELEHNTER Wechsel zählt hier ausdrücklich **nicht** mit; er
     * erscheint als nicht blockierender Befund `altstatus_wechsel_unerlaubt`
     * in `altimport_befund` und wird von `pnpm altimport:befunde` gezählt.
     */
    altstatusNachgezogen: z.number().int().nonnegative(),
    /**
     * Schon vorhandene Belege der **gebuchten** Klasse, an denen dieser Lauf
     * mit dem Altstatus-Nachzug zugleich die **Forderungsbuchung** nachgeholt
     * hat (`AI-nachzugbuchung`, 2026-09-17, Migration `0239`) — eine
     * **Teilmenge von `altstatusNachgezogen`** und damit von `schonVorhanden`.
     *
     * **Warum es sie braucht.** Ein Beleg der gebuchten Klasse, den das
     * Altsystem als Entwurf beziehungsweise Fehler führt (`legacy_status =
     * -1`), entsteht ohne Journalbuchung (`0175`, `E-936`). Setzt das
     * Altsystem ihn später auf `1`, fällt diese Ausnahme weg, und die Buchung
     * wird fällig — sonst weist Prüfung 3d aus `assert_document_consistent()`
     * (`document_archive_posted`) den ganzen Push ab. Genau das ist am
     * 2026-09-17 auf `accounting-stg` passiert.
     *
     * **Eigene Zahl und nicht in `altstatusNachgezogen` versteckt**, aus
     * demselben Grund wie bei `einloesungenGebucht`: Sie zählt eine
     * JOURNALBUCHUNG und keinen Feldnachtrag. Ein Cron-Lauf, der drei
     * Statuswechsel nachzieht und dabei drei Forderungen bucht, ist etwas
     * anderes als einer, der drei Rechnungen von offen auf bezahlt setzt — und
     * ohne diese Zahl im `fax_fehler`-Bericht des Altsystems nicht davon zu
     * unterscheiden.
     *
     * Sie bleibt `0` für jeden Wechsel, der nicht buchen darf oder nichts zu
     * buchen hat: die Archivklasse (Prüfung 3c verbietet dort die Buchung),
     * ein Bruttobetrag von 0,00 (`E-895`), ein Beleg, der seine Buchung schon
     * trägt (`1 → 5`, `1 → 4`), und ein wiederholter Push (der Statuswechsel
     * lautet dann `unveraendert`).
     */
    forderungNachgebucht: z.number().int().nonnegative(),
    /**
     * Schon vorhandene Gutschriftbelege, an denen `predecessor_id` in diesem
     * Lauf nachgetragen wurde (`AI-gutschriftbezug`, Migration `0184`) —
     * wieder eine **Teilmenge von `schonVorhanden`** und deshalb neben der
     * Gleichung.
     *
     * **Eigene Zahl aus demselben Grund wie bei `zahlwegNachgetragen`:** Der
     * Nachtrag trifft eine fünfte, eigene Belegmenge — Gutschriften, deren
     * `fa_gutschrift.invoice_id` sich zu einem bereits importierten Beleg
     * auflösen lässt (gemessen alle fünf des Bestands vom 2026-09-03). In
     * einem gewöhnlichen Neuaufbau ist sie `0`: Dort löst der Zubringer den
     * Bezug schon beim ERSTEN Anlegen der Gutschrift auf, weil die Rechnung
     * dann längst importiert ist. Erst ein Delta-Lauf mit geänderter
     * Reihenfolge — oder eine Gutschrift, deren Zielrechnung erst NACH ihr
     * eingespielt wurde — füllt sie.
     */
    gutschriftzielNachgetragen: z.number().int().nonnegative(),
    kundenEmailGesetzt: z.number().int().nonnegative(),
    kundenEmailNachgetragen: z.number().int().nonnegative(),
    /**
     * Guthaben-Einlösungen (`1796` an `1400`), die in diesem Lauf entstanden
     * sind (`AI-einloesungpush`, 2026-09-10, Entscheidung `D-33` der
     * Cutover-Spec) — die einzige Zahl des Berichts, die eine JOURNALBUCHUNG
     * zählt und keinen Beleg.
     *
     * **Sie steht neben der Gleichung und in keiner ihrer Hälften.** Eine
     * Einlösung entsteht an einem Beleg, der entweder soeben angelegt wurde
     * oder schon da war; sie ist damit keine dritte Belegklasse, sondern eine
     * Folge — wer sie in `angelegt` oder `schonVorhanden` aufnähme, zählte
     * denselben Beleg zweimal.
     *
     * **Warum es sie gibt.** Im Parallelbetrieb bis zum 2026-09-30 meldet das
     * Altsystem eine per Guthaben bezahlte Rechnung über diesen Endpunkt
     * nach; bis zu diesem Auftrag buchte ihre Einlösung ausschliesslich der
     * Cutover-Läufer aus dem Dump, und eine gepushte Rechnung bekam sie
     * deshalb nie. Ohne diese Zahl wäre ein Lauf, der zehn Einlösungen bucht,
     * von einem, der keine bucht, nicht zu unterscheiden.
     *
     * Ein **wiederholter** Push zählt nicht mit: Die Einlösung steht dann
     * schon, und der Idempotenzschlüssel gibt sie zurück, statt eine zweite zu
     * schreiben. Eine fällige, aber nicht gebuchte Einlösung erscheint als
     * nicht blockierender Befund `einloesung_nicht_gebucht` in
     * `altimport_befund` und wird von `pnpm altimport:befunde` gezählt.
     */
    einloesungenGebucht: z.number().int().nonnegative(),
    /**
     * Belege, an denen `altlink_hash_sha256` in diesem Lauf gesetzt wurde
     * (`KP-altlinkpush`, 2026-09-25) — beim Anlegen UND beim Wiedereinstieg an
     * einem schon vorhandenen Beleg, der noch keinen trug. Neben der Gleichung
     * und in keiner ihrer Hälften: Ein Beleg bekommt den Hash, er wird nicht
     * durch ihn zu einem zweiten.
     *
     * Ein Beleg, der denselben Hash schon trägt, zählt NICHT mit — ein
     * wiederholter Push meldet hier `0`.
     */
    altlinkGesetzt: z.number().int().nonnegative(),
    /**
     * Belege, die schon einen ANDEREN Altlink-Hash tragen als den gepushten.
     * Nichts wird überschrieben (`0234`, Einmal-Setzen-Schleife); der Fall
     * gehört vor einen Menschen. Es gibt dafür bewusst keine Befundart in
     * `altimport_befund` — eine neue Art bräuchte eine Migration, und die
     * Zählung hier plus {@link altimportBerichtSchema.shape.altlinkBefunde}
     * nennt den Beleg ohnehin.
     */
    altlinkAbweichend: z.number().int().nonnegative(),
    /**
     * Belege, deren gepushter Hash schon einem ANDEREN Beleg gehört — der
     * partielle eindeutige Index `document_altlink_hash_unique` (`0234`) weist
     * ihn ab. Gezählt statt als Stapelabbruch: Der Beleg selbst entsteht bzw.
     * bleibt, nur ohne Altlink.
     */
    altlinkDoppelt: z.number().int().nonnegative(),
    /**
     * Die Fremdschlüssel hinter `altlinkAbweichend` und `altlinkDoppelt`, je
     * mit ihrer Art — nie ein Hash. Leer im Regelfall.
     */
    altlinkBefunde: z.array(z
        .object({
        fremdschluessel: z.string(),
        ergebnis: altimportAltlinkErgebnisSchema.extract(['abweichend', 'doppelt']),
    })
        .strict()),
})
    .strict();
/**
 * Ein Eintrag des Nachschickstapels: welcher Altbeleg welchen Altlink-Hash
 * bekommt.
 *
 * `fremdschluessel` ist dieselbe Alt-`id` als Text, die der Push als
 * `fremdschluessel` schickt und die `document.source_reference` trägt — für
 * alle drei Belegarten die `id` der EIGENEN Alt-Tabelle (`fa_rechnungen.id`,
 * `fa_storno.id`, `fa_gutschrift.id`), nie `fa_storno.rechnung_id`.
 */
export const altimportAltlinkEintragSchema = z
    .object({
    fremdschluessel: pflichtText('fremdschluessel'),
    altlinkHashSha256: altlinkHashSha256Schema,
})
    .strict();
/**
 * Der Rumpf von `POST /api/v1/invoices/import/altlinks` — höchstens 500
 * Einträge, dieselbe Stapelgrenze wie beim Belegstapel und aus demselben
 * Grund: eine Transaktion, eine Beratungssperre.
 */
export const altimportAltlinkRequestSchema = z
    .object({
    eintraege: z
        .array(altimportAltlinkEintragSchema)
        .min(1, 'Der Stapel enthält keinen Eintrag')
        .max(500, 'Höchstens 500 Einträge je Stapel'),
})
    .strict();
/**
 * Die Antwort des Nachschickstapels: je Eintrag sein Ergebnis, in der
 * Reihenfolge des Rumpfs, dazu die Summen.
 *
 * **`gelesen` ist die Summe der fünf Ergebniszahlen** — dieselbe Bauart wie
 * der Prüfbericht des Imports: Ein Lauf, der nur „fertig" sagt, ist von einem,
 * der die Hälfte still übersprungen hat, nicht zu unterscheiden.
 *
 * Die Antwort nennt Fremdschlüssel und nie einen Hash — auch nicht den
 * gepushten SHA-256, den der Absender ohnehin kennt.
 */
export const altimportAltlinkAntwortSchema = z
    .object({
    gelesen: z.number().int().nonnegative(),
    gesetzt: z.number().int().nonnegative(),
    unveraendert: z.number().int().nonnegative(),
    unbekannt: z.number().int().nonnegative(),
    abweichend: z.number().int().nonnegative(),
    doppelt: z.number().int().nonnegative(),
    eintraege: z.array(z
        .object({
        fremdschluessel: z.string(),
        ergebnis: altimportAltlinkErgebnisSchema,
    })
        .strict()),
})
    .strict();
/**
 * Ein Tupel, aus dem **kein Beleg** wurde, samt seiner Befunde — der Weg
 * „Befund ohne Beleg" (Aufgabe `AI-steuerbild`, Spec 7.3 Nachtrag vom
 * 2026-09-03).
 *
 * **Warum es diesen zweiten Weg überhaupt gibt.** `altimport_befund` ist die
 * zählbare rechte Seite der Vollständigkeitsgleichung `importierte Belege +
 * blockierende Fehllistenzeilen = 11.260` (`D-22`, Spec 10.1) — und der Dienst
 * schrieb seine Zeilen bis hierher ausschliesslich **aus einem Beleg**
 * (`AltimportBeleg`). Ein Tupel, das `altimportBelegSchema` gar nicht besteht
 * (Anschrift ohne ISO-Code, kein einziger Positionseintrag), erreichte die
 * Fehlliste damit nie: kein Beleg auf der linken Seite, keine Zeile auf der
 * rechten, und die Gleichung war um genau diese Tupel falsch, ohne dass es
 * irgendwo aufgefallen wäre. Gemessen über den echten Dump am 2026-09-03
 * (Archivklasse, nur Zählungen): **65 Tupel**.
 *
 * **Die Zeile trägt weniger als ein Beleg, und das ist der Punkt.** Kein
 * Belegdatum, keine Anschrift, keine Positionen — genau das fehlt ja. Was
 * bleibt, ist der Bezug in den Dump (`fremdschluessel`) und der Grund
 * (`befunde`), und das ist zugleich alles, was `altimport_befund` an Spalten
 * hat.
 *
 * **Was sie NICHT trägt und warum:** die `nummernfolge`. Sie hat in
 * `altimport_befund` keine Spalte, und dieser Vertrag nimmt kein Feld
 * entgegen, das kein Ziel hat (Kopftext dieser Datei). Folge, die benannt
 * gehört: Der Rechnungsnummernzähler wird für ein solches Tupel **nicht**
 * fortgeschrieben (`D-5`, `E-640`) — trüge das höchste Tupel des Dumps einen
 * blockierenden Befund, bliebe `document_no_invoice` hinter ihm zurück.
 * `pnpm altimport:zaehler-check` misst den Abstand gegen die importierten
 * Belege und sähe das nicht.
 *
 * **`belegart` seit `I-befundquelle` (2026-09-03).** `AltimportService`
 * schrieb `altimport_befund.quelle` bis dahin fest auf `'fa_rechnungen'`,
 * auch für Tupel aus `fa_storno` und `fa_gutschrift` — Migration `0120` sieht
 * in der Spalte ausdrücklich alle drei Alt-Tabellennamen vor
 * (`altimport_befund_quelle_shape`). Das Feld hier ist derselbe Schnitt wie
 * bei {@link altimportBelegSchema}: `.default('rechnung')`, damit ein
 * Aufrufer ohne diese Angabe sich wortgleich wie vorher verhält.
 */
export const altimportBefundzeileSchema = z
    .object({
    /** Die Alt-`id` als Text — wird `altimport_befund.fremdschluessel`. */
    fremdschluessel: pflichtText('fremdschluessel'),
    /**
     * Mindestens ein Befund. Eine Zeile ohne Grund wäre eine Behauptung über
     * ein Tupel, die nichts sagt — und sie schriebe nichts, während der
     * Aufrufer sie zählte.
     */
    befunde: z
        .array(altimportBefundSchema)
        .min(1, 'Ein Tupel ohne Beleg braucht mindestens einen Befund')
        .max(50, 'Zu viele Befunde an einem Tupel'),
    /**
     * Welche Alt-Tabelle das Tupel geliefert hat ({@link altimportBelegartSchema}).
     * Bestimmt `altimport_befund.quelle` (`rechnung` → `fa_rechnungen`,
     * `storno` → `fa_storno`, `gutschrift` → `fa_gutschrift`).
     */
    belegart: altimportBelegartSchema.default('rechnung'),
})
    .strict();
/**
 * Ein Stapel solcher Tupel — dieselbe Stapelgrösse wie beim Belegstapel und
 * aus demselben Grund (eine Transaktion, eine Beratungssperre).
 */
export const altimportBefundStapelSchema = z
    .object({
    zeilen: z
        .array(altimportBefundzeileSchema)
        .min(1, 'Der Stapel enthält kein Tupel')
        .max(500, 'Höchstens 500 Tupel je Stapel'),
})
    .strict();
/**
 * Der Prüfbericht des Befundstapels.
 *
 * | Zahl | Bedeutung |
 * |---|---|
 * | `gelesen` | Tupel im Stapel |
 * | `zeilen` | Fehllistenzeilen, die der Stapel geschrieben oder schon vorgefunden hat |
 *
 * **`zeilen` zählt Fehllistenzeilen und nicht Tupel** — anders als
 * `AltimportBericht.befunde`, das Belege zählt. Beide Einheiten werden
 * gebraucht: Die Vollständigkeitsgleichung zählt Tupel (`DISTINCT
 * fremdschluessel`), `altimport:befunde` zählt Zeilen je Art.
 */
export const altimportBefundStapelBerichtSchema = z
    .object({
    gelesen: z.number().int().nonnegative(),
    zeilen: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Kopfzeile, in der der Absender die Prüfsumme des geschobenen
 * Archivstücks nennt — `PUT /api/v1/invoices/import/{fremdschluessel}/pdf`
 * (Aufgabe `AI-pdfpush`, Spec-Nachtrag vom 2026-09-09, `D-31`).
 *
 * **Kleingeschrieben, weil Node die Kopfzeilen kleingeschrieben führt.** Der
 * Wert selbst ist die hexadezimale, kleingeschriebene SHA-256 der Bytes —
 * dieselbe Form wie `document.pdf_sha256` (`document_pdf_sha_shape`).
 *
 * **Warum eine Kopfzeile und kein Feld im Rumpf.** Der Rumpf IST die Datei
 * (dieselbe Bauform wie beim Auszugsupload der Bankanbindung und beim
 * Archivstück eines Eingangsbelegs); ein Umschlag mit Feldern brächte eine
 * zweite Kodierung und damit eine zweite Stelle, an der Bytes verändert werden
 * könnten, bevor jemand ihre Prüfsumme rechnet.
 */
export const ALTIMPORT_PDF_PUSH_HASH_KOPFZEILE = 'x-content-sha256';
/**
 * Was mit dem geschobenen Archivstück geschehen ist.
 *
 * | Wert | Bedeutung |
 * |---|---|
 * | `abgelegt` | Der Beleg trug noch kein Archivstück; die Bytes liegen jetzt im Archiv, und `pdf_object_key`/`pdf_sha256` stehen am Beleg |
 * | `schonVorhanden` | Der Beleg trug bereits GENAU DIESES Stück (gleiche Prüfsumme). Es wurde nichts geändert |
 *
 * **Es gibt bewusst keinen dritten Wert für „ersetzt".** Ein Archivstück wird
 * nie überschrieben (§ 147 Abs. 1 AO, GoBD Rz. 130 f.); ein abweichendes
 * zweites Stück am selben Beleg ist eine Ablehnung
 * (`ALTIMPORT_PDF_ALREADY_STORED`) und kein Zustand.
 */
export const ALTIMPORT_PDF_PUSH_STAENDE = ['abgelegt', 'schonVorhanden'];
export const altimportPdfPushStandSchema = z.enum(ALTIMPORT_PDF_PUSH_STAENDE);
/**
 * Die Antwort auf einen geschobenen Archivbeleg.
 *
 * **`sha256` ist der GERECHNETE Wert und nicht der mitgeschickte.** Beide sind
 * an dieser Stelle gleich — sonst wäre die Anfrage mit
 * `ALTIMPORT_PDF_HASH_MISMATCH` abgewiesen worden —, und der gerechnete ist
 * der, für den dieses System einsteht. Ein zurückgegebener Kopfzeilenwert
 * sagte nur, was der Absender behauptet hat.
 *
 * **`bytes` steht daneben, damit die Quittung ohne einen zweiten Aufruf
 * lesbar ist.** Ein Push, der 0 Byte ablegte, käme gar nicht so weit
 * (`ALTIMPORT_PDF_NOT_A_PDF` greift vorher); die Zahl ist die Auskunft, an der
 * ein abgeschnittener Upload im Protokoll des Absenders auffällt.
 */
export const altimportPdfPushAntwortSchema = z
    .object({
    stand: altimportPdfPushStandSchema,
    sha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'Die Prüfsumme ist keine kleingeschriebene SHA-256 in Hex'),
    bytes: z.number().int().positive(),
})
    .strict();
//# sourceMappingURL=altimport.js.map